<script setup lang="ts">
import { onMounted, ref } from "vue";
import {
    usePaymentGateway,
    billingDetailsSchema,
    type BillingDetails,
} from "../composables/usePaymentGateway";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { useForm as useInertiaForm } from "@inertiajs/vue3";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import type { BillingAddress } from "../types";
import CountrySelect from "./CountrySelect.vue";
import RequiredStarIcon from "./RequiredStarIcon.svg";
import route from "ziggy-js";

interface Props {
    teamId?: number;
    clientSecret: string;
    billingAddress: BillingAddress;
}

const props = defineProps<Props>();
const emit = defineEmits(["method-added", "close"]);
const { addPaymentMethod, init } = usePaymentGateway(props.clientSecret);

const formSchema = toTypedSchema(billingDetailsSchema);

const addMethodForm = useInertiaForm<{ paymentMethod: string | null }>({
    paymentMethod: null,
});

const loading = ref(false);

const prepareName = (address: BillingAddress) => {
    if (!address.first_name || !address.last_name) {
        return "";
    }

    return `${address.first_name} ${address.last_name}`;
};

const form = useForm<BillingDetails>({
    validationSchema: formSchema,
    initialValues: {
        name: prepareName(props.billingAddress),
        country: props.billingAddress.country.code,
        postal_code: props.billingAddress.postal_code ?? "",
    },
});

const addMethod = form.handleSubmit(async (values) => {
    loading.value = true;
    const paymentId = await addPaymentMethod({
        name: values.name,
        postal_code: values.postal_code,
        country: values.country,
    });

    if (typeof paymentId !== "string") {
        loading.value = false;
        return;
    }

    addMethodForm.paymentMethod = paymentId;

    addMethodForm.post(
        route("app.payment-methods.create", { team: props.teamId }),
        {
            onSuccess: () => {
                loading.value = false;
                emit("method-added");
            },
        },
    );
});

onMounted(() => {
    init();
});
</script>

<template>
    <div class="mt-8 w-full">
        <FormField v-slot="{ componentField }" name="name">
            <FormItem v-auto-animate>
                <FormLabel class="gap-1 flex items-center"
                    >{{ $t("billing.card.full_name") }}

                    <RequiredStarIcon />
                </FormLabel>
                <FormControl>
                    <Input type="text" v-bind="componentField" />
                </FormControl>
                <FormMessage />
            </FormItem>
        </FormField>

        <div class="mt-5 gap-3 grid grid-cols-2">
            <FormField v-slot="{ value, handleChange }" name="country">
                <FormItem>
                    <CountrySelect
                        required
                        :model-value="value as string"
                        @update:model-value="handleChange"
                    />
                    <FormMessage />
                </FormItem>
            </FormField>
            <div class="gap-1.5 grid items-center">
                <FormField v-slot="{ componentField }" name="postal_code">
                    <FormItem v-auto-animate>
                        <FormLabel class="gap-1 flex items-center"
                            >{{ $t("billing.billing_information.zip") }}

                            <RequiredStarIcon />
                        </FormLabel>
                        <FormControl>
                            <Input type="text" v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
            </div>
        </div>

        <Label class="mb-2 mt-6 gap-1 flex items-center" for="postal_code"
            >{{ $t("billing.card.card_details") }}

            <RequiredStarIcon />
        </Label>

        <div
            id="card-number"
            class="rounded-lg border-iron-300 p-3 h-[43px] w-full border"
        >
            <!-- A Stripe Element will be inserted here. -->
        </div>

        <div class="mt-5 gap-3 flex">
            <div>
                <Label class="mb-2 gap-1 flex items-center" for="card-expiry">
                    {{ $t("billing.card.expiry") }}

                    <RequiredStarIcon />
                </Label>

                <div
                    id="card-expiry"
                    class="rounded-lg border-iron-300 p-3 col-span-1 w-[124px] border"
                >
                    <!-- A Stripe Element will be inserted here. -->
                </div>
            </div>

            <div>
                <Label class="mb-2 gap-1 flex items-center" for="card-cvc"
                    >CVC

                    <RequiredStarIcon />
                </Label>
                <div
                    id="card-cvc"
                    class="rounded-lg border-iron-300 p-3 col-span-1 w-[108px] border"
                >
                    <!-- A Stripe Element will be inserted here. -->
                </div>
            </div>
        </div>
    </div>

    <div class="mb-4 mt-6 gap-4 flex justify-end">
        <Button @click="emit('close')" variant="outline">{{
            $t("go_back")
        }}</Button>
        <Button :loading @click="addMethod">
            {{ $t("billing.card.add_payment_method") }}</Button
        >
    </div>
</template>
